import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { AppState, PaymentTier, UserProfile, UserRole } from '../types';
import {
  GOOGLE_CLIENT_ID,
  GOOGLE_SCOPES,
  GOOGLE_DISCOVERY_DOC,
  STORAGE_KEYS,
  BETA_INVITE_CODE,
  BETA_GATE_ENABLED,
} from '../config/constants';
import { supabase } from '../services/supabaseClient';
import { resetTabVerification, setTokenRefresher } from '../services/googleSheetsService';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextType {
  appState: AppState;
  setAppState: (state: AppState) => void;
  isSignedIn: boolean;
  isLoading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  sheetId: string | null;
  setSheetId: (id: string | null) => void;
  isReady: boolean;
  paymentTier: PaymentTier;
  userRole: UserRole;
  isPro: boolean;
  toggleDevTier: (() => void) | null;
  approveBetaAccess: (code: string) => Promise<boolean>;
  refreshProfile: () => Promise<void>;
  handleSignIn: () => void;
  handleSignOut: () => Promise<void>;
  handleDeleteAccount: () => Promise<void>;
  user: User | null;
  userProfile: UserProfile | null;
  stripeReturnStatus: 'success' | null;
  clearStripeReturnStatus: () => void;
  googleTokenFailed: boolean;
  sheetsScopeMissing: boolean;
  reconnectGoogle: () => void;
  refreshGoogleToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// --- Google Token Cache Helpers ---

const TOKEN_CACHE_MAX_AGE_MS = 55 * 60 * 1000; // 55 minutes
const TOKEN_REFRESH_INTERVAL_MS = 45 * 60 * 1000; // refresh before Google’s ~60m expiry

function isHardRefreshFailure(code?: string): boolean {
  return code === 'NO_REFRESH_TOKEN' || code === 'invalid_grant';
}

function saveGoogleTokenToCache(token: string): void {
  try {
    localStorage.setItem(
      STORAGE_KEYS.GOOGLE_ACCESS_TOKEN,
      JSON.stringify({ token, timestamp: Date.now() })
    );
  } catch {
    // localStorage full or unavailable
  }
}

function loadGoogleTokenFromCache(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.GOOGLE_ACCESS_TOKEN);
    if (!raw) return null;
    const { token, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > TOKEN_CACHE_MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEYS.GOOGLE_ACCESS_TOKEN);
      return null;
    }
    return token as string;
  } catch {
    return null;
  }
}

function clearGoogleTokenCache(): void {
  localStorage.removeItem(STORAGE_KEYS.GOOGLE_ACCESS_TOKEN);
  resetTabVerification();
}

// Redirect-loop protection: clear stale marker on successful token acquisition
const GIS_REDIRECT_KEY = 'tallyup_gis_auto_redirect';

function clearGisAutoRedirect(): void {
  sessionStorage.removeItem(GIS_REDIRECT_KEY);
}

async function validateGoogleToken(token: string): Promise<boolean> {
  try {
    const res = await fetch(
      `https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=${encodeURIComponent(token)}`
    );
    if (!res.ok) return false;
    const info = await res.json();
    // Ensure token belongs to our app and has >60s remaining
    if (info.audience !== GOOGLE_CLIENT_ID) return false;
    if (Number(info.expires_in) < 60) return false;
    return true;
  } catch {
    return false;
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Detect stored session + cached sheet_id for instant dashboard render on refresh
  const [appState, setAppState] = useState<AppState>(() => {
    // If the URL contains an OAuth callback (hash for implicit, query code for PKCE),
    // don't flash the landing or dashboard — Supabase will exchange/parse it momentarily
    const hash = window.location.hash;
    if (hash && (hash.includes('access_token=') || hash.includes('refresh_token='))) {
      return 'loading';
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('code')) {
      return 'loading';
    }
    const hasSheetId = !!localStorage.getItem(STORAGE_KEYS.SHEET_ID);
    const betaApproved = localStorage.getItem(STORAGE_KEYS.BETA_APPROVED) === 'true';
    // If no sheet_id but user has a Supabase session, show loading (not landing)
    // so returning users who didn't finish onboarding don't see a flash of the landing page
    if (!hasSheetId) {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) return 'loading';
      }
      return 'landing';
    }
    // Only fast-path to dashboard if beta is approved
    if (!betaApproved) return 'loading';
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) return 'dashboard';
    }
    return 'landing';
  });
  const [sheetId, setSheetIdState] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEYS.SHEET_ID)
  );
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isLoading] = useState(false);
  const [gapiInited, setGapiInited] = useState(false);
  const [hasGoogleToken, setHasGoogleToken] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rawPaymentTier, setRawPaymentTier] = useState<PaymentTier>('free');
  const [userRole, setUserRole] = useState<UserRole>('user');
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [stripeReturnStatus, setStripeReturnStatus] = useState<'success' | null>(null);
  const [googleTokenFailed, setGoogleTokenFailed] = useState(false);
  const [sheetsScopeMissing, setSheetsScopeMissing] = useState(false);
  const [devTierOverride, setDevTierOverride] = useState<PaymentTier | null>(null);

  // Queue provider_token if gapi isn't loaded yet
  const pendingGoogleTokenRef = useRef<string | null>(null);
  // Track sign-in state without causing effect re-runs
  const isSignedInRef = useRef(false);

  // Data loading gates on ALL three: auth ready, gapi loaded, AND Google token acquired
  const isReady = isAuthReady && gapiInited && hasGoogleToken;

  // Developers and admins always get Pro features (unless dev override is active)
  const isDeveloper = userRole === 'developer' || userRole === 'admin';
  const effectivePaymentTier: PaymentTier =
    isDeveloper && devTierOverride !== null
      ? devTierOverride
      : isDeveloper
        ? 'pro'
        : rawPaymentTier;
  const isPro = effectivePaymentTier === 'pro';

  const toggleDevTier: (() => void) | null = isDeveloper
    ? () => setDevTierOverride((prev) => (prev === 'free' ? null : 'free'))
    : null;

  // --- Profile helpers ---

  const loadOrCreateProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    const { data, error: fetchError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (fetchError) {
      if (fetchError.code === 'PGRST116') {
        await new Promise((r) => setTimeout(r, 1000));
        const { data: retryData, error: retryError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();
        if (retryError) {
          console.error('Failed to load profile after retry:', retryError);
          return null;
        }
        return retryData as UserProfile;
      }
      console.error('Failed to load profile:', fetchError);
      return null;
    }
    return data as UserProfile;
  }, []);

  const updateProfileField = useCallback(async (userId: string, fields: Partial<Pick<UserProfile, 'sheet_id' | 'tutorial_completed'>>) => {
    const { error: updateError } = await supabase
      .from('profiles')
      .update(fields)
      .eq('id', userId);

    if (updateError) {
      console.error('Failed to update profile:', updateError);
    }
  }, []);

  // --- refreshProfile ---

  const refreshProfile = useCallback(async () => {
    if (!user?.id) return;
    const profile = await loadOrCreateProfile(user.id);
    if (profile) {
      setUserProfile(profile);
      setRawPaymentTier(profile.payment_tier || 'free');
      setUserRole(profile.role || 'user');
      if (profile.tutorial_completed) {
        localStorage.setItem(STORAGE_KEYS.TUTORIAL_COMPLETED, 'true');
      } else if (localStorage.getItem(STORAGE_KEYS.TUTORIAL_COMPLETED) === 'true' && user?.id) {
        supabase.from('profiles').update({ tutorial_completed: true }).eq('id', user.id)
          .then(() => {});
      }
    }
  }, [user?.id, loadOrCreateProfile]);

  // --- setSheetId wrapper that persists to Supabase ---

  const setSheetId = useCallback((id: string | null) => {
    setSheetIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEYS.SHEET_ID, id);
    }
    if (user?.id && id) {
      updateProfileField(user.id, { sheet_id: id });
    }
  }, [user?.id, updateProfileField]);

  // --- Apply Google token to gapi.client ---

  const applyGoogleToken = useCallback((accessToken: string) => {
    saveGoogleTokenToCache(accessToken);
    if (window.gapi?.client) {
      window.gapi.client.setToken({ access_token: accessToken } as unknown as string);
      setHasGoogleToken(true);
    } else {
      pendingGoogleTokenRef.current = accessToken;
    }

    // Verify the Sheets scope was actually granted
    fetch(`https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=${encodeURIComponent(accessToken)}`)
      .then(res => {
        if (!res.ok) return null; // tokeninfo failed (e.g. 400) — don't block
        return res.json();
      })
      .then(info => {
        if (!info) return; // skip scope check if tokeninfo request failed
        const grantedScopes: string = info.scope || '';
        if (!grantedScopes.includes('drive.file')) {
          setSheetsScopeMissing(true);
          setHasGoogleToken(false);
        } else {
          setSheetsScopeMissing(false);
        }
      })
      .catch(() => { /* tokeninfo failed — don't block, scope will fail at API call time */ });
  }, []);

  // --- Server-side token refresh via Edge Function ---
  type TokenRefreshResult =
    | { ok: true; accessToken: string }
    | { ok: false; errorCode?: string };

  const refreshTokenViaServer = useCallback(async (): Promise<TokenRefreshResult> => {
    try {
      // Refresh the Supabase JWT to get a valid access token
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !refreshData.session) {
        console.warn('[TokenRefresh] Supabase session refresh failed:', refreshError?.message || 'no session');
        return { ok: false };
      }

      const jwt = refreshData.session.access_token;
      const functionsUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/refresh-google-token`;

      const res = await fetch(functionsUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({}),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error(`[TokenRefresh] Edge Function returned ${res.status}:`, errorText);
        try {
          const body = JSON.parse(errorText) as { code?: string };
          return { ok: false, errorCode: body.code };
        } catch {
          return { ok: false };
        }
      }

      const data = await res.json();
      if (data.access_token) {
        console.log('[TokenRefresh] Got fresh Google token from Edge Function');
        return { ok: true, accessToken: data.access_token };
      }
      console.warn('[TokenRefresh] Edge Function returned no access_token:', data);
    } catch (err) {
      console.error('[TokenRefresh] Edge Function call failed:', err);
    }
    return { ok: false };
  }, []);

  // --- Register token refresher for googleSheetsService (401 auto-retry) ---
  useEffect(() => {
    setTokenRefresher(async () => {
      const result = await refreshTokenViaServer();
      if (result.ok) {
        applyGoogleToken(result.accessToken);
        setGoogleTokenFailed(false);
        return;
      }

      const cached = loadGoogleTokenFromCache();
      if (cached && (await validateGoogleToken(cached))) {
        applyGoogleToken(cached);
        return;
      }

      if (isHardRefreshFailure(result.errorCode)) {
        setGoogleTokenFailed(true);
        setError(null);
        throw new Error('GOOGLE_RECONNECT_REQUIRED');
      }

      throw new Error('GOOGLE_TOKEN_REFRESH_FAILED');
    });
  }, [applyGoogleToken, refreshTokenViaServer]);

  // --- Apply tokens from a fresh Google OAuth redirect (sign-in or reconnect) ---
  const handleOAuthTokens = useCallback((session: Session) => {
    setError(null);
    setGoogleTokenFailed(false);

    if (session.provider_token) {
      clearGisAutoRedirect();
      applyGoogleToken(session.provider_token);
    }

    const providerRefreshToken = (session as unknown as { provider_refresh_token?: string }).provider_refresh_token;
    if (providerRefreshToken) {
      supabase.functions
        .invoke('store-google-token', { body: { refresh_token: providerRefreshToken } })
        .then(({ error: storeError }) => {
          if (storeError) console.error('Failed to store Google refresh token:', storeError);
        });
    }
  }, [applyGoogleToken]);

  // --- Post sign-in handler ---

  const handlePostSignIn = useCallback((session: Session) => {
    const supaUser = session.user;
    setUser(supaUser);
    handleOAuthTokens(session);

    // Use cached sheet_id from localStorage for instant dashboard
    const cachedSheetId = localStorage.getItem(STORAGE_KEYS.SHEET_ID);

    setIsSignedIn(true);
    isSignedInRef.current = true;

    // Load profile and enforce beta gate
    loadOrCreateProfile(supaUser.id).then(async (profile) => {
      if (profile) {
        setUserProfile(profile);
        setRawPaymentTier(profile.payment_tier || 'free');
        setUserRole(profile.role || 'user');

        // Sync tutorial completion between Supabase and localStorage
        if (profile.tutorial_completed) {
          localStorage.setItem(STORAGE_KEYS.TUTORIAL_COMPLETED, 'true');
        } else if (localStorage.getItem(STORAGE_KEYS.TUTORIAL_COMPLETED) === 'true') {
          // Existing user completed tutorial before Supabase sync — push it up
          supabase.from('profiles').update({ tutorial_completed: true }).eq('id', supaUser.id)
            .then(() => {});
        }

        // Beta gate check — localStorage is the primary gate (set before OAuth redirect)
        const cachedBetaApproved = localStorage.getItem(STORAGE_KEYS.BETA_APPROVED) === 'true';
        const isBetaApproved = !BETA_GATE_ENABLED || profile.beta_approved ||
          cachedBetaApproved;

        if (!isBetaApproved) {
          localStorage.removeItem(STORAGE_KEYS.BETA_APPROVED);
          setAppState('beta-gate');
          return;
        }

        // Cache beta approval for fast refresh
        localStorage.setItem(STORAGE_KEYS.BETA_APPROVED, 'true');

        // Terms acceptance gate — always check Supabase profile (not localStorage)
        if (!profile.terms_accepted_at) {
          setAppState('terms-gate');
          return;
        }

        if (profile.sheet_id) {
          if (profile.sheet_id !== cachedSheetId) {
            setSheetIdState(profile.sheet_id);
            localStorage.setItem(STORAGE_KEYS.SHEET_ID, profile.sheet_id);
          }
          setAppState('dashboard');
        } else if (!cachedSheetId) {
          // payment_tier is written only by the Stripe webhook. A tier left in
          // localStorage by an older build is discarded, never trusted.
          localStorage.removeItem(STORAGE_KEYS.PAYMENT_TIER);
          setAppState('onboarding');
        } else {
          // cachedSheetId exists, use it
          setSheetIdState(cachedSheetId);
          setAppState('dashboard');
        }
      } else if (!cachedSheetId) {
        setAppState('onboarding');
      }
    }).catch((err) => {
      console.error('Background profile load failed:', err);
      if (!cachedSheetId) {
        setAppState('onboarding');
      }
    });
  }, [loadOrCreateProfile, handleOAuthTokens]);

  // --- Load GAPI + GIS scripts ---

  useEffect(() => {
    if (window.gapi?.client?.sheets) {
      setGapiInited(true);
      if (pendingGoogleTokenRef.current) {
        window.gapi.client.setToken({ access_token: pendingGoogleTokenRef.current } as unknown as string);
        setHasGoogleToken(true);
        pendingGoogleTokenRef.current = null;
      }
    } else {
      const gapiScript = document.createElement('script');
      gapiScript.src = 'https://apis.google.com/js/api.js';
      gapiScript.async = true;
      gapiScript.defer = true;
      gapiScript.onload = () => {
        window.gapi.load('client', async () => {
          await window.gapi.client.load(GOOGLE_DISCOVERY_DOC);
          if (pendingGoogleTokenRef.current) {
            window.gapi.client.setToken({ access_token: pendingGoogleTokenRef.current } as unknown as string);
            setHasGoogleToken(true);
            pendingGoogleTokenRef.current = null;
          }

          setGapiInited(true);
        });
      };
      document.body.appendChild(gapiScript);
    }

    if (!window.google?.accounts?.oauth2) {
      const gisScript = document.createElement('script');
      gisScript.src = 'https://accounts.google.com/gsi/client';
      gisScript.async = true;
      gisScript.defer = true;
      gisScript.onload = () => {};
      document.body.appendChild(gisScript);
    }
  }, []);

  // --- Acquire Google token: cache-first, then server-side refresh ---
  // Fires when: user is signed in + GAPI is loaded + we don't have a Google token yet

  useEffect(() => {
    if (!isSignedIn || !gapiInited || hasGoogleToken) return;

    // If handlePostSignIn already applied a token but gapi wasn't ready yet,
    // the token is queued in pendingGoogleTokenRef and will be applied when gapi loads.
    if (pendingGoogleTokenRef.current) return;

    let cancelled = false;

    const acquireToken = async () => {
      // 1. Try cached token
      const cached = loadGoogleTokenFromCache();
      if (cached) {
        const valid = await validateGoogleToken(cached);
        if (!cancelled && valid) {
          applyGoogleToken(cached);
          return;
        }
        if (!cancelled) {
          clearGoogleTokenCache();
        }
      }

      // 2. Try server-side refresh via Edge Function
      let refreshErrorCode: string | undefined;
      if (!cancelled) {
        const result = await refreshTokenViaServer();
        refreshErrorCode = result.ok ? undefined : result.errorCode;
        if (!cancelled && result.ok) {
          applyGoogleToken(result.accessToken);
          return;
        }
      }

      // 3. If server-side failed, only prompt reconnect when refresh is truly dead
      if (!cancelled && isHardRefreshFailure(refreshErrorCode)) {
        setGoogleTokenFailed(true);
      }
    };

    acquireToken();

    return () => { cancelled = true; };
  }, [isSignedIn, gapiInited, hasGoogleToken, applyGoogleToken, refreshTokenViaServer]);

  // --- Proactive token refresh: interval + visibility ---
  // Keeps long-lived tabs alive by refreshing before the ~60m Google access token expires.

  useEffect(() => {
    if (!isSignedIn || !gapiInited || !hasGoogleToken) return;

    const doProactiveRefresh = async () => {
      const result = await refreshTokenViaServer();
      if (result.ok) {
        applyGoogleToken(result.accessToken);
        setGoogleTokenFailed(false);
        return;
      }

      // Server refresh failed — keep using the current token if it still works.
      const cached = loadGoogleTokenFromCache();
      if (cached && (await validateGoogleToken(cached))) {
        applyGoogleToken(cached);
        return;
      }

      if (isHardRefreshFailure(result.errorCode)) {
        setGoogleTokenFailed(true);
        setError(null);
      }
    };

    const validateAndRefreshIfNeeded = async () => {
      const cached = loadGoogleTokenFromCache();
      if (cached) {
        const valid = await validateGoogleToken(cached);
        if (valid) return;
        clearGoogleTokenCache();
      }
      await doProactiveRefresh();
    };

    // Refresh on an interval so tabs left open overnight stay connected.
    const intervalId = window.setInterval(() => {
      doProactiveRefresh();
    }, TOKEN_REFRESH_INTERVAL_MS);

    // Also refresh when the tab regains focus if the cached token is missing or stale.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!isSignedInRef.current) return;
      validateAndRefreshIfNeeded();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // First refresh timed from cache age so we don't wait a full interval after load.
    const cached = localStorage.getItem(STORAGE_KEYS.GOOGLE_ACCESS_TOKEN);
    let timeUntilRefresh = TOKEN_REFRESH_INTERVAL_MS;
    if (cached) {
      try {
        const { timestamp } = JSON.parse(cached);
        const elapsed = Date.now() - timestamp;
        timeUntilRefresh = Math.max(TOKEN_REFRESH_INTERVAL_MS - elapsed, 60_000);
      } catch { /* use default */ }
    }

    const timerId = window.setTimeout(() => {
      doProactiveRefresh();
    }, timeUntilRefresh);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timerId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSignedIn, gapiInited, hasGoogleToken, applyGoogleToken, refreshTokenViaServer]);

  // --- Detect Stripe checkout return via URL param ---

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');

    if (sessionId) {
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);

      setStripeReturnStatus('success');

      const pollProfile = async () => {
        for (let i = 0; i < 5; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          // Read the id from the live session, not the `user` captured when
          // this effect ran: on the return from Stripe that is usually still
          // null, and the effect never re-runs once session_id is stripped.
          const { data: { session } } = await supabase.auth.getSession();
          const userId = session?.user?.id;
          if (userId) {
            const profile = await loadOrCreateProfile(userId);
            if (profile && profile.payment_tier === 'pro') {
              setUserProfile(profile);
              setRawPaymentTier('pro');
              return;
            }
          }
        }
      };

      pollProfile();
    }
  }, [user?.id, loadOrCreateProfile]);

  // --- Supabase auth state listener ---

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if ((event === 'INITIAL_SESSION' || event === 'SIGNED_IN') && session) {
          setTimeout(() => {
            const hasOAuthTokens = !!(
              session.provider_token ||
              (session as unknown as { provider_refresh_token?: string }).provider_refresh_token
            );

            // Reconnect while already signed in — capture new Google tokens without re-running full sign-in
            if (event === 'SIGNED_IN' && hasOAuthTokens && isSignedInRef.current) {
              handleOAuthTokens(session);
              setIsAuthReady(true);
              return;
            }

            // Dedup INITIAL_SESSION+SIGNED_IN double-firing on init, BUT always
            // re-run when a fresh provider_token arrives (post-Reconnect OAuth callback).
            if (isSignedInRef.current && !session.provider_token) return;

            handlePostSignIn(session);
            setIsAuthReady(true);
          }, 0);
        } else if (event === 'INITIAL_SESSION' && !session) {
          setAppState('landing');
          setSheetIdState(null);
          setIsAuthReady(true);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setUserProfile(null);
          setIsSignedIn(false);
          isSignedInRef.current = false;
          setHasGoogleToken(false);
          setGoogleTokenFailed(false);
          setSheetIdState(null);
          setRawPaymentTier('free');
          setUserRole('user');
          setAppState('landing');
          localStorage.removeItem(STORAGE_KEYS.SHEET_ID);
          clearGoogleTokenCache();
          if (window.gapi?.client) {
            window.gapi.client.setToken('' as unknown as string);
          }
        }
        // Note: TOKEN_REFRESHED is a Supabase JWT refresh — it never carries
        // provider_token (Google OAuth token). Google token refresh is handled
        // separately via the server-side Edge Function.
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [handlePostSignIn, handleOAuthTokens, applyGoogleToken]);

  // --- Sign in via Supabase OAuth (redirect) ---

  const handleSignIn = useCallback(() => {
    supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        scopes: GOOGLE_SCOPES,
        redirectTo: window.location.origin,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
  }, []);

  // --- Refresh Google token (called when API returns 401) ---

  const refreshGoogleToken = useCallback(async () => {
    setGoogleTokenFailed(false);

    const result = await refreshTokenViaServer();
    if (result.ok) {
      applyGoogleToken(result.accessToken);
      return;
    }

    clearGoogleTokenCache();
    if (window.gapi?.client) {
      window.gapi.client.setToken('' as unknown as string);
    }
    setHasGoogleToken(false);

    if (isHardRefreshFailure(result.errorCode)) {
      setGoogleTokenFailed(true);
    }
  }, [applyGoogleToken, refreshTokenViaServer]);

  // --- Reconnect Google (full OAuth redirect) ---

  const reconnectGoogle = useCallback(() => {
    supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        scopes: GOOGLE_SCOPES,
        redirectTo: window.location.origin,
        queryParams: { access_type: 'offline', prompt: 'consent' },
      },
    });
  }, []);

  // --- Sign out ---

  const handleSignOut = useCallback(async () => {
    clearGoogleTokenCache();
    const token = window.gapi?.client?.getToken?.();
    if (token?.access_token) {
      try {
        window.google.accounts.oauth2.revoke(token.access_token);
      } catch {
        // GIS may not be loaded
      }
      window.gapi.client.setToken('' as unknown as string);
    }
    await supabase.auth.signOut();
  }, []);

  // --- Delete account ---

  const handleDeleteAccount = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No active session');
    }

    const res = await supabase.functions.invoke('delete-account', {
      body: {},
    });

    if (res.error) {
      throw new Error(res.error.message || 'Failed to delete account');
    }

    const body = res.data;
    if (body?.error) {
      throw new Error(body.error);
    }

    // Clean up local state (don't call supabase.auth.signOut — user is already deleted)
    clearGoogleTokenCache();
    const token = window.gapi?.client?.getToken?.();
    if (token?.access_token) {
      try {
        window.google.accounts.oauth2.revoke(token.access_token);
      } catch {
        // GIS may not be loaded
      }
      window.gapi.client.setToken('' as unknown as string);
    }
    // Clear ALL local state for a clean re-signup
    localStorage.removeItem(STORAGE_KEYS.SHEET_ID);
    localStorage.removeItem(STORAGE_KEYS.TUTORIAL_COMPLETED);
    setUser(null);
    setUserProfile(null);
    setIsSignedIn(false);
    isSignedInRef.current = false;
    setHasGoogleToken(false);
    setGoogleTokenFailed(false);
    setSheetsScopeMissing(false);
    setIsAuthReady(false);
    setSheetIdState(null);
    setRawPaymentTier('free');
    setUserRole('user');
    setAppState('landing');
    // Force-clear Supabase's stored session token from localStorage
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        localStorage.removeItem(key);
      }
    }
  }, []);

  // --- Approve beta access via invite code ---

  const approveBetaAccess = useCallback(async (code: string): Promise<boolean> => {
    if (code.trim().toUpperCase() !== BETA_INVITE_CODE) {
      return false;
    }

    // Cache approval in localStorage — this is the primary gate
    localStorage.setItem(STORAGE_KEYS.BETA_APPROVED, 'true');
    return true;
  }, []);

  // --- Clear Stripe return status ---

  const clearStripeReturnStatus = useCallback(() => {
    setStripeReturnStatus(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        appState,
        setAppState,
        isSignedIn,
        isLoading,
        error,
        setError,
        sheetId,
        setSheetId,
        isReady,
        paymentTier: effectivePaymentTier,
        userRole,
        isPro,
        toggleDevTier,
        approveBetaAccess,
        refreshProfile,
        handleSignIn,
        handleSignOut,
        handleDeleteAccount,
        user,
        userProfile,
        stripeReturnStatus,
        clearStripeReturnStatus,
        googleTokenFailed,
        sheetsScopeMissing,
        reconnectGoogle,
        refreshGoogleToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
